/**
 * Remembers where someone was heading when they got bounced to the login page,
 * so they land there after signing in instead of on /landing.
 *
 * This exists because of emailed deep links. A release note awaiting approval
 * links straight to /release-notes?review=v1.7.0; without this the approver
 * signs in, arrives at /landing, and the link may as well not have carried the
 * version at all.
 *
 * sessionStorage rather than localStorage: a pending redirect belongs to this
 * tab and this sign-in attempt. Left in localStorage it would fire days later
 * in an unrelated tab.
 */

const KEY = "postLoginRedirect";

// Same-origin app paths only. A stored value starting "//" or "http:" would be
// an open redirect — <Navigate> would happily send the user off-site with a
// freshly minted session.
const isSafeAppPath = (p) =>
  typeof p === "string" &&
  p.startsWith("/") &&
  !p.startsWith("//") &&
  !p.includes("\\");

// Landing on these after login is the default anyway; storing them would only
// add a redirect hop.
const NOT_WORTH_REMEMBERING = ["/", "/landing"];

export const rememberDestination = (pathname, search = "") => {
  const full = `${pathname || ""}${search || ""}`;
  if (!isSafeAppPath(full)) return;
  if (NOT_WORTH_REMEMBERING.includes(pathname)) return;
  try {
    sessionStorage.setItem(KEY, full);
  } catch {
    // Private browsing / storage disabled. The deep link degrades to /landing,
    // which is the behaviour we had before — not worth failing the login over.
  }
};

/** Returns the remembered path once, then forgets it. */
export const consumeDestination = () => {
  try {
    const target = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    return isSafeAppPath(target) ? target : null;
  } catch {
    return null;
  }
};
